<template>
  <modal
    centered
    @close="$emit('close')"
  >
    <template slot="body">
      <b-row class="px-1">
        <b-col
          cols="12"
          class="text-center"
        >
          <BaseTextH2>{{ athleteName }}</BaseTextH2>
        </b-col>
        <b-col
          :cols="12"
          class="d-flex justify-content-center py-4"
        >
          <BaseAvatar
            :url="athleteAvatar"
            :size="130"
          />
        </b-col>
        <b-col
          :cols="4"
          class="d-flex align-items-center justify-content-center text-center"
        >
          <b-img
            :src="teamImage"
            fluid
            :class="$style.teamImage"
          />
          <BaseTextSmall class="px-2">
            {{ teamName }}
          </BaseTextSmall>
        </b-col>
        <b-col
          :cols="4"
          class="d-flex align-items-center justify-content-center text-center"
        >
          <font-awesome-icon
            :icon="['far', 'clock']"
            size="lg"
          />
          <BaseTextSmall class="px-2">
            {{ time }}
          </BaseTextSmall>
        </b-col>
        <b-col
          :cols="4"
          class="d-flex align-items-center justify-content-center text-center"
        >
          <font-awesome-icon
            :icon="['far', 'futbol']"
            size="lg"
          />
          <BaseTextSmall class="px-2">
            {{ categoryText }}
          </BaseTextSmall>
        </b-col>
        <b-col
          :cols="12"
          class="p-3 py-4"
        >
          <BaseVideo
            :poster="gif.image_url"
            @play="handleVideoPlay"
          >
            <source
              :src="gif.video_webm_url"
              type="video/webm;codecs=&quot;vp8, vorbis&quot;"
            >
            <source
              :src="gif.video_mp4_url"
              type="video/mp4;codecs=&quot;avc1.42E01E, mp4a.40.2&quot;"
            >
          </BaseVideo>
        </b-col>
        <b-col
          :cols="12"
          class="d-flex justify-content-center py-3"
        >
          <CopyIcon
            v-if="enabledShareProviders.includes('copy')"
            v-clipboard:copy="shareBeUrl"
          />
          <FacebookIcon
            v-if="enabledShareProviders.includes('facebook')"
            @click="handleFacebookClick"
          />
          <WeiboIcon
            v-if="enabledShareProviders.includes('weibo')"
            @click="handleWeiboClick"
          />
          <QqIcon
            v-if="enabledShareProviders.includes('qq')"
            @click="handleQqClick"
          />
        </b-col>
        <b-col
          :cols="12"
          class="d-flex justify-content-center py-3"
        >
          <BaseButton
            is-rounded
            @click="$emit('close')"
          >
            <span class="px-5">
              {{ $t('close') }}
            </span>
          </BaseButton>
        </b-col>
      </b-row>
    </template>
  </modal>
</template>

<script>
import { mapActions } from 'vuex'
import Modal from '@/components/Molecules/Modal'
import BaseAvatar from '@/components/Atoms/BaseAvatar'
import BaseTextH2 from '@/components/Atoms/Typography/BaseTextH2'
import BaseTextSmall from '@/components/Atoms/Typography/BaseTextSmall'
import BaseButton from '@/components/Atoms/BaseButton'
import BaseVideo from '@/components/Atoms/BaseVideo'
import WeiboIcon from '@/components/Atoms/Icons/WeiboIcon'
import QqIcon from '@/components/Atoms/Icons/QqIcon'
import CopyIcon from '@/components/Atoms/Icons/CopyIcon'
import FacebookIcon from '@/components/Atoms/Icons/FacebookIcon'

export default {
  name: 'TheAthletePerformanceGif',
  components: {
    FacebookIcon,
    CopyIcon,
    QqIcon,
    WeiboIcon,
    BaseVideo,
    BaseButton,
    BaseTextSmall,
    BaseTextH2,
    BaseAvatar,
    Modal,
  },
  props: {
    athleteName: {
      type: String,
      required: true,
    },
    athleteAvatar: {
      type: String,
      required: true,
    },
    categoryText: {
      type: String,
      required: true,
    },
    teamName: {
      type: String,
      required: true,
    },
    teamImage: {
      type: String,
      required: true,
    },
    time: {
      type: String,
      required: true,
    },
    gif: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      providers: {
        facebook: 'https://www.facebook.com/sharer/sharer.php?u=@url',
        qq: 'http://sns.qzone.qq.com/cgi-bin/qzshare/cgi_qzshare_onekey?url=@url&title=@title&pics=@image',
        weibo: 'http://v.t.sina.com.cn/share/share.php?title=@title+@url&pic=@image',
      },
    }
  },
  computed: {
    enabledShareProviders() {
      const providers = process.env.VUE_APP_SHARE_PROVIDERS
      return providers ? providers.split(',') : []
    },
    shareUiPayload() {
      return Buffer.from(JSON.stringify({
        1: this.gif.image_url,
        2: this.gif.video_mp4_url,
      })).toString('base64')
    },
    shareBeUrl() {
      return `${window.location.origin}/${this.gif.match.slug}/videos/${this.gif.time}-minute`
    },
    shareTitle() {
      return `Pick8.net ${this.$t('game-highlight')} - ${this.categoryText}: ${this.athleteName} (${this.time})`
    },
    shareUiUrl() {
      const route = { name: 'ShareGif', params: { payload: this.shareUiPayload } }
      const path = this.$router.resolve(route).href
      return `${window.location.origin}${path}`
    },
  },
  methods: {
    ...mapActions({
      incrementPlayedCount: 'matches/matchGifs/incrementPlayedCount',
    }),
    handleFacebookClick() {
      this.openPopup({
        url: this.providers.facebook.replace('@url', this.shareBeUrl),
      })
    },
    handleVideoPlay() {
      this.incrementPlayedCount()
    },
    handleQqClick() {
      this.openPopup({
        url: this.providers.qq
          .replace('@title', this.shareTitle)
          .replace('@url', this.shareBeUrl)
          .replace('@image', this.gif.image_url),
      })
    },
    handleWeiboClick() {
      this.openPopup({
        url: this.providers.weibo
          .replace('@title', this.shareTitle)
          .replace('@url', this.shareBeUrl)
          .replace('@image', this.gif.image_url),
      })
    },
    openPopup({
      url,
      height = 700,
      width = 450,
    }) {
      const popup = window.open(
        url,
        'sharer',
        `height=${height},width=${width}`
      )
      popup.window.focus()
    },
  },
}
</script>

<style module lang="scss">
  .teamImage {
    max-height: 21px;
  }
</style>
